/**
 * O fundo de água, com o mergulho.
 *
 * A matemática do shader vem intacta de `agua.glsl.ts`, e a física da câmera
 * vem intacta do painel antigo. Nada aqui é curva de bézier: a câmera ganha
 * velocidade por integração, com gravidade e arrasto —
 *
 *     v += (g - k*v) * dt
 *     z += v * dt
 *
 * O arrasto é o que faz a descida tender a uma velocidade terminal em vez de
 * disparar. É o que dá peso ao movimento, como afundar em água densa. Os
 * valores foram achados em várias rodadas de ajuste com o usuário; mexer neles
 * é mexer na identidade.
 *
 * O escuro entra só depois da metade do trajeto, e o véu preto acompanha a
 * MESMA variável do shader: os dois fecham juntos, sem defasagem entre canvas
 * e sobreposição. É o que garante que nunca exista um quadro com as duas telas
 * visíveis ao mesmo tempo.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { FRAGMENT, VERTEX } from "./agua.glsl";
import css from "./Agua.module.css";

const G = 0.62; // aceleração
const K = 0.72; // arrasto (velocidade terminal ~ G/K)
const Z_FIM = 1.35; // profundidade que encerra o mergulho
const ESCURO_EM = 0.5; // fração do trajeto em que a luz começa a cair
const EMERSAO_MS = 700;
/** Quem pediu menos movimento ganha a água mais lenta, não o fundo chapado. */
const RITMO_REDUZIDO = 0.35;

/* A ÁGUA CLARA (19/10). O tema claro do sistema (`data-tema="claro"` na raiz,
 * posto pela moldura) liga a paleta clara que o shader sempre teve — `uClaro`,
 * "água rasa e ensolarada" —, e a troca é um fade de uns 400ms, não um corte.
 * A varredura é sempre o abismo: ali a água volta ao escuro NA HORA, sem fade,
 * porque ela chega coberta pelo véu do mergulho e um fade apareceria depois
 * que ele sai. */
const temaClaro = () => document.documentElement.dataset.tema === "claro";
/** Quanto do caminho até o tema novo a água anda a cada quadro (~400ms a 60fps). */
const PASSO_DO_TEMA = 0.12;

/** A cor do véu do mergulho. No escuro, preto, como sempre. No claro, o azul do
 *  sistema no começo e o abismo da varredura no fim — a tela clara atravessa
 *  o azul e chega ao mesmo escuro de onde a varredura começa, sem degrau. */
const AZUL = [28, 58, 170];
const ABISMO = [2, 4, 31];
function corDoVeu(v: number, claro: number) {
  if (claro < 0.5) return "#000";
  const k = Math.min(Math.max((v - 0.55) / 0.45, 0), 1);
  const s = k * k * (3 - 2 * k);
  const [r, g, b] = AZUL.map((a, i) => Math.round(a + (ABISMO[i]! - a) * s));
  return `rgb(${r}, ${g}, ${b})`;
}

/* A ÁGUA LEVE NO CELULAR (2026-10-02, "a navegação muito lenta"). O shader é
 * pesado e corria a 60 quadros por segundo com até 2x a densidade da tela —
 * num celular, ~1,3 milhão de pixels por quadro disputando a GPU com a
 * rolagem. No toque: um orçamento de pixels (a água é macia; ampliada pelo
 * CSS, não se nota), 30 quadros por segundo, e a água PARADA enquanto o dedo
 * rola a página. O mesmo critério do `tokens.css`, que tira o desfoque. */
const TOQUE = "(hover: none) and (pointer: coarse)";
const PIXELS_NO_TOQUE = 160_000;
const QUADRO_NO_TOQUE_MS = 1000 / 30;
/** Quanto tempo depois do último gesto a água volta a correr. */
const PAUSA_DO_GESTO_MS = 250;

interface Controle {
  /** Verdadeiro durante a descida. A moldura some junto com o hero. */
  mergulhando: boolean;
  /** Desce até o escuro total. Resolve quando a tela já está fechada. */
  mergulhar: () => Promise<void>;
  /** Recua a câmera de volta ao repouso, sem clarear a água — o abismo
   *  continua o cenário depois do mergulho, só sem a câmera colada nele. */
  emergir: () => void;
  /** Volta ao abismo, sem transição. */
  repousar: () => void;
  /** Liga o abismo reluzente da varredura: mesma água, luz liberada. */
  fossa: (ligada: boolean) => void;
}

const semAgua: Controle = {
  mergulhando: false,
  mergulhar: () => Promise.resolve(),
  emergir: () => {},
  repousar: () => {},
  fossa: () => {},
};
const Contexto = createContext<Controle>(semAgua);

export const useAgua = () => useContext(Contexto);

/** Cria o shader e manda compilar, SEM perguntar se compilou. Perguntar
 *  (COMPILE_STATUS, LINK_STATUS) trava a página até o compilador terminar — e
 *  no Windows o Chrome traduz o shader para o DirectX, que leva segundos num
 *  shader do tamanho deste. Quem pergunta é `preparar()`, dentro do quadro,
 *  quando a resposta já não trava. */
function criar(gl: WebGLRenderingContext, tipo: number, fonte: string) {
  const s = gl.createShader(tipo)!;
  gl.shaderSource(s, fonte);
  gl.compileShader(s);
  return s;
}

export function ProvedorAgua({ children }: { children: ReactNode }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const preto = useRef<HTMLDivElement>(null);
  // A câmera vive em refs, não em estado: ela muda a cada quadro, e um
  // setState por quadro renderizaria a árvore inteira 60 vezes por segundo.
  const camera = useRef({ mergulho: 0, escuro: 0, claro: temaClaro() ? 1 : 0, fossa: 0 });
  const parado = useRef(false);
  const [mergulhando, setMergulhando] = useState(false);

  useEffect(() => {
    const tela = canvas.current;
    if (!tela) return;

    const gl = tela.getContext("webgl", {
      antialias: false,
      alpha: false,
    }) as WebGLRenderingContext | null;
    // Sem WebGL a página não fica preta: o CSS do container pinta a mesma
    // paleta em estático, e o canvas continua invisível (opacity 0).
    if (!gl) return;

    // A COMPILAÇÃO NÃO TRAVA A TELA (2026-09-29). Com as cáusticas o shader
    // cresceu, e a tela ficava PRETA até compilar — o canvas é opaco
    // (alpha: false), então antes do primeiro quadro ele é um retângulo preto
    // por cima do degradê de reserva. Agora ele nasce invisível; com
    // KHR_parallel_shader_compile o shader compila em segundo plano e cada
    // quadro só pergunta se já terminou; o canvas entra em fade depois do
    // primeiro desenho. Sem a extensão, a pergunta trava como antes, mas
    // dentro do primeiro quadro, e o degradê segura a tela até lá.
    const paralelo = gl.getExtension("KHR_parallel_shader_compile");
    const vs = criar(gl, gl.VERTEX_SHADER, VERTEX);
    const fs = criar(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);

    // Um triângulo que cobre a tela inteira: mais barato que dois de um quad,
    // porque nenhum pixel é processado duas vezes na diagonal.
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW,
    );

    type Uniformes = Record<
      "res" | "tempo" | "mergulho" | "escuro" | "claro" | "fossa",
      WebGLUniformLocation | null
    >;
    let u: Uniformes | null = null;
    let estado: "compilando" | "pronto" | "falhou" = "compilando";

    function preparar() {
      if (paralelo && !gl!.getProgramParameter(prog, paralelo.COMPLETION_STATUS_KHR)) {
        return; // ainda compilando em segundo plano
      }
      if (!gl!.getProgramParameter(prog, gl!.LINK_STATUS)) {
        console.error("shader:", gl!.getShaderInfoLog(vs), gl!.getShaderInfoLog(fs));
        console.error("programa:", gl!.getProgramInfoLog(prog));
        estado = "falhou"; // o canvas continua invisível; fica o degradê
        return;
      }
      gl!.useProgram(prog);
      const p = gl!.getAttribLocation(prog, "p");
      gl!.enableVertexAttribArray(p);
      gl!.vertexAttribPointer(p, 2, gl!.FLOAT, false, 0, 0);
      u = {
        res: gl!.getUniformLocation(prog, "uRes"),
        tempo: gl!.getUniformLocation(prog, "uTime"),
        mergulho: gl!.getUniformLocation(prog, "uMergulho"),
        escuro: gl!.getUniformLocation(prog, "uEscuro"),
        claro: gl!.getUniformLocation(prog, "uClaro"),
        fossa: gl!.getUniformLocation(prog, "uFossa"),
      };
      estado = "pronto";
      medir(true);
    }

    parado.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ritmo = parado.current ? RITMO_REDUZIDO : 1;
    const toque = window.matchMedia(TOQUE);

    function medir(forcar = false) {
      const largura = tela!.clientWidth || window.innerWidth;
      const altura = tela!.clientHeight || window.innerHeight;
      let d = Math.min(window.devicePixelRatio || 1, 2);
      if (toque.matches) d = Math.min(d, Math.sqrt(PIXELS_NO_TOQUE / (largura * altura)));
      const w = Math.max(1, Math.floor(largura * d));
      const h = Math.max(1, Math.floor(altura * d));
      if (w !== tela!.width || h !== tela!.height) {
        tela!.width = w;
        tela!.height = h;
        gl!.viewport(0, 0, w, h);
        forcar = true;
      }
      if (forcar && u) gl!.uniform2f(u.res, w, h);
    }
    medir();
    const observador = new ResizeObserver(() => medir());
    observador.observe(tela);
    const trocaToque = () => medir();
    toque.addEventListener("change", trocaToque);

    let pedido = 0;
    let desenhados = 0;
    let ultimoDesenho = -Infinity;
    let ultimoGesto = -Infinity;
    // `scroll` não sobe na árvore, mas passa pelo document na captura: assim
    // pega a rolagem de qualquer painel, não só a da página.
    const marcaGesto = () => {
      ultimoGesto = performance.now();
    };
    const escuta = { capture: true, passive: true } as const;
    document.addEventListener("scroll", marcaGesto, escuta);
    window.addEventListener("touchmove", marcaGesto, escuta);

    /** No toque, nem todo quadro desenha: 30 por segundo, e nenhum enquanto o
     *  dedo rola. Sem desenho novo, a tela mostra o último quadro. Os dois
     *  primeiros desenhos nunca esperam — são eles que fazem o canvas aparecer. */
    function desenhaAgora(agora: number) {
      if (!toque.matches || desenhados < 2) return true;
      if (agora - ultimoGesto < PAUSA_DO_GESTO_MS) return false;
      return agora - ultimoDesenho >= QUADRO_NO_TOQUE_MS - 2;
    }
    // CADA VISITA COMEÇA NUM MOMENTO DIFERENTE DO FLUXO (2026-09-29). O tempo
    // da água começava do zero a cada carregamento, e o campo anda devagar:
    // os primeiros minutos eram sempre a MESMA arrumação, com a luz do lado
    // direito — a borda esquerda tinha luz em 7% do tempo, medido. Até 20
    // minutos de fluxo à frente, sorteado a cada abertura da página.
    const inicio = performance.now() - Math.random() * 20 * 60 * 1000;
    function quadro(agora: number) {
      if (estado === "compilando") preparar();
      if (estado === "falhou") return;
      if (estado === "pronto" && u && desenhaAgora(agora)) {
        ultimoDesenho = agora;
        const c = camera.current;
        // O TEMA: a varredura (fossa) é sempre o abismo, e volta a ele na hora;
        // fora dela a água anda até o tema da raiz em poucos quadros.
        const alvo = c.fossa >= 1 ? 0 : temaClaro() ? 1 : 0;
        if (c.fossa >= 1 || parado.current) c.claro = alvo;
        else {
          c.claro += (alvo - c.claro) * PASSO_DO_TEMA;
          if (Math.abs(alvo - c.claro) < 0.002) c.claro = alvo;
        }
        gl!.uniform1f(u.tempo, ((agora - inicio) / 1000) * ritmo);
        gl!.uniform1f(u.mergulho, c.mergulho);
        gl!.uniform1f(u.escuro, c.escuro);
        gl!.uniform1f(u.claro, c.claro);
        gl!.uniform1f(u.fossa, c.fossa);
        gl!.drawArrays(gl!.TRIANGLES, 0, 3);
        // Aparece no quadro SEGUINTE ao primeiro desenho: o navegador só
        // chama o próximo quadro depois de apresentar o anterior.
        if (++desenhados === 2) tela!.style.opacity = "1";
      }
      pedido = requestAnimationFrame(quadro);
    }
    pedido = requestAnimationFrame(quadro);

    // Aba escondida não precisa de água — é bateria de notebook em reunião.
    function visibilidade() {
      cancelAnimationFrame(pedido);
      if (!document.hidden) pedido = requestAnimationFrame(quadro);
    }
    document.addEventListener("visibilitychange", visibilidade);

    return () => {
      cancelAnimationFrame(pedido);
      document.removeEventListener("visibilitychange", visibilidade);
      document.removeEventListener("scroll", marcaGesto, escuta);
      window.removeEventListener("touchmove", marcaGesto, escuta);
      toque.removeEventListener("change", trocaToque);
      observador.disconnect();
      gl.deleteProgram(prog);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteBuffer(buf);
    };
  }, []);

  const aplicarPreto = (v: number) => {
    if (!preto.current) return;
    preto.current.style.opacity = String(v);
    preto.current.style.background = corDoVeu(v, camera.current.claro);
  };

  const mergulhar = useCallback(
    () =>
      new Promise<void>((pronto) => {
        setMergulhando(true);
        const c = camera.current;
        // Movimento reduzido: o mergulho é deslocamento de câmera de verdade,
        // e é exatamente o que a preferência pede para suprimir. A tela fecha
        // do mesmo jeito, só sem a viagem.
        if (parado.current) {
          c.escuro = 1;
          aplicarPreto(1);
          pronto();
          return;
        }
        let v = 0;
        let z = 0;
        let anterior = performance.now();
        const passo = (agora: number) => {
          // dt com teto: aba em segundo plano devolve saltos enormes, e sem o
          // limite a câmera pularia o trajeto inteiro num quadro só.
          const dt = Math.min((agora - anterior) / 1000, 1 / 30);
          anterior = agora;
          v += (G - K * v) * dt;
          z += v * dt;
          c.mergulho = z;
          const fracao = z / Z_FIM;
          c.escuro = Math.min(
            Math.max((fracao - ESCURO_EM) / (1 - ESCURO_EM), 0),
            1,
          );
          aplicarPreto(c.escuro);
          if (z < Z_FIM) requestAnimationFrame(passo);
          else {
            c.mergulho = Z_FIM;
            c.escuro = 1;
            aplicarPreto(1);
            pronto();
          }
        };
        requestAnimationFrame(passo);
      }),
    [],
  );

  /** De onde a emersao PARTE, e nao de onde o mergulho parou.
   *
   *  Medido quadro a quadro: com a camera ainda funda, a tela da varredura
   *  fica literalmente vazia — 0,0% de presenca e relevo zero enquanto
   *  `mergulho` passa de 0.5. O campo esta ampliado 8x (o zoom e
   *  1 + mergulho * 5.5) e escurecido pela profundidade, entao nao existe
   *  ondulacao para ver: chega-se do mergulho num quadro preto, e a agua so
   *  aparece no finalzinho do trajeto de volta.
   *
   *  Recuar a partir de 0.10 preserva o assentamento da camera — ainda ha
   *  movimento de volta, ele so nao comeca do fundo do poco — e a ondulacao
   *  ja esta na tela no primeiro quadro: 10,5% de presenca contra 0,0%.
   *
   *  A descida NAO muda: `mergulhar` continua indo ate Z_FIM. Isto e so o
   *  ponto de partida da volta. */
  const EMERSAO_DE = 0.1;

  const emergir = useCallback(() => {
    setMergulhando(false);
    const c = camera.current;
    c.escuro = 0;
    c.mergulho = Math.min(c.mergulho, EMERSAO_DE);
    // `claro` NÃO é tocado aqui: quem o comanda é o tema da raiz, a cada
    // quadro (ver `temaClaro`), e a varredura — que é quem emerge — o leva ao
    // abismo pela `fossa`.
    aplicarPreto(0);
    const t0 = performance.now();
    const z0 = c.mergulho;
    const passo = (agora: number) => {
      const k = parado.current ? 1 : Math.min((agora - t0) / EMERSAO_MS, 1);
      const suave = 1 - Math.pow(1 - k, 3);
      c.mergulho = z0 * (1 - suave);
      if (k < 1) requestAnimationFrame(passo);
    };
    requestAnimationFrame(passo);
  }, []);

  const repousar = useCallback(() => {
    setMergulhando(false);
    // `fossa` sobrevive ao repousar: quem a liga é a rota, não o estado
    // da câmera, e a varredura chama repousar() ao sair para desfazer o
    // mergulho — zerar a fossa aqui apagaria o brilho no meio da tela.
    // `claro` fica como está: é o tema que o comanda. Zerá-lo aqui faria a home
    // clara piscar escura a cada vez que ela monta.
    camera.current = { ...camera.current, mergulho: 0, escuro: 0 };
    aplicarPreto(0);
  }, []);

  const fossa = useCallback((ligada: boolean) => {
    camera.current.fossa = ligada ? 1 : 0;
  }, []);

  return (
    <Contexto.Provider value={{ mergulhando, mergulhar, emergir, repousar, fossa }}>
      <div className={css.fundo} aria-hidden="true">
        <canvas ref={canvas} className={css.tela} />
        <div className={css.veu} />
      </div>
      {/* O véu preto acompanha a mesma variável do shader. Fica fora do
          container do fundo porque precisa cobrir o conteúdo também. */}
      <div ref={preto} className={css.preto} aria-hidden="true" />
      {children}
    </Contexto.Provider>
  );
}
